import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, FileText, Map, RefreshCw } from '@onward-delivery/design/icons';
import { AUTO_ASSIGN } from '@/constants/routes';
import { useClientUser } from '@/hooks';
import { CopyableId } from '../../modals/blocks';
import { defaultDraftKeyFor, setupDraftKeyFor } from '../../modals/IntegrationsModal';
import { CorpAdminContext } from '../../store';
import {
    defaultIntegrationCardFor,
    defaultPricingCardFor,
    isFlexCenterRow,
    isFulfillmentPartnerRow,
    NO_DEFAULT_PRICING,
} from '../utils';
import DocumentsCard from './DocumentsCard';
import FlexCenterShippers from './FlexCenterShippers';
import LinkedShippers from './LinkedShippers';
import SettingCard from './SettingCard';
import ShipperSettingModals from './ShipperSettingModals';

const CARD_ICONS = {
    integration: RefreshCw,
    pricing: DollarSign,
    default_pricing: DollarSign,
    delivery_profile: Map,
    rate_addendum: FileText,
};

const CARRIER_WIDE_CARDS = new Set(['delivery_profile', 'default_pricing']);

const NO_LINKED_SHIPPERS_COPY =
    'No shippers linked yet. The default tariff and integration below cover every shipper in your org.';

const NO_FLEX_CENTER_SHIPPERS_COPY =
    'No shippers linked yet. A delivery profile that maps this Flex Center links them.';

const SECTION_BLOCK_CLASS = 'flex flex-col gap-4 rounded-lg border border-slate-200 p-4';

const SectionHeading = ({ children }) => (
    <span className="text-[11px] font-bold uppercase text-slate-500">{children}</span>
);

const ExpandedCarrier = ({
    row,
    shipperGroups = [],
    marketplaceShippers = [],
    flexCenters = [],
    contractors,
    defaultPricing = NO_DEFAULT_PRICING,
    defaultIntegrations = [],
    onOpenContractor,
}) => {
    const navigate = useNavigate();
    const { callbacks } = useContext(CorpAdminContext);
    const { organization_id } = useClientUser();
    const [openModal, setOpenModal] = useState(null);

    // Looked up on every render so a modal reflects the store after its own mutation lands.
    const shippers = shipperGroups.flatMap((group) => group.shippers);
    // A contractor's pricing is per Flex Center, which the modals take in the shipper's place.
    let pricingPartners = shippers;
    if (row.isContractor) pricingPartners = flexCenters;
    if (row.isMarketplace) pricingPartners = marketplaceShippers;
    const modalShipper = pricingPartners.find((partner) => partner.client_id === openModal?.shipperId);
    // Pricing a contractor from its Flex Center's row opens the contractor's modal, not this row's.
    const modalContractor = contractors?.find(
        (contractor) => contractor.contractorRow.client_id === openModal?.contractorId
    );

    const openCard = (card, shipper) => {
        if (card.key === 'delivery_profile') {
            navigate(AUTO_ASSIGN);
            return;
        }

        if (card.key === 'default_pricing') {
            setOpenModal({ cardKey: 'default_pricing' });
            return;
        }

        // With a default, the modal offers to customize it instead.
        const needsIntegration =
            card.key === 'integration' && !shipper.settings.integrations.length && !defaultIntegrations.length;
        if (needsIntegration) {
            const draftKey = setupDraftKeyFor(shipper.client_id, row.client_id);
            const draft = {
                carrier_id: row.client_id,
                client_id: row.client_id,
                shipper_id: shipper.client_id,
                type: 'MANUAL',
            };
            // An open draft may hold the id of a row a failed save wrote, so reopening keeps it.
            callbacks.setCustomizeDrafts((drafts) => (drafts[draftKey] ? drafts : { ...drafts, [draftKey]: draft }));
        }

        setOpenModal({ cardKey: card.key, shipperId: shipper.client_id });
    };

    const firstPositionByGroup = shipperGroups.reduce((positions, group, index) => {
        const previous = shipperGroups[index - 1];
        positions.push(previous ? positions[index - 1] + previous.shippers.length : 0);

        return positions;
    }, []);

    const openDefaultIntegration = () => {
        if (!defaultIntegrations.length) {
            // A row with neither org nor shipper reads at runtime as every shipper's.
            if (!organization_id) {
                callbacks?.setNotification?.({
                    severity: 'error',
                    message: 'A default integration needs an organization',
                });
                return;
            }

            const draftKey = defaultDraftKeyFor(row.client_id);
            const draft = {
                carrier_id: row.client_id,
                client_id: row.client_id,
                organization_id,
                is_shipper_default: true,
                type: 'MANUAL',
            };
            callbacks.setCustomizeDrafts((drafts) => (drafts[draftKey] ? drafts : { ...drafts, [draftKey]: draft }));
        }

        setOpenModal({ cardKey: 'default_integration' });
    };

    const defaultPricingCard = defaultPricingCardFor(defaultPricing);
    const defaultIntegrationCard = defaultIntegrationCardFor(defaultIntegrations);

    // A quarter of the row each, the width they have beside the other cards.
    const defaultCards = (
        <div className="grid grid-cols-4 gap-4">
            <SettingCard
                card={defaultIntegrationCard}
                icon={CARD_ICONS.integration}
                onAction={openDefaultIntegration}
            />
            <SettingCard
                card={defaultPricingCard}
                icon={CARD_ICONS.pricing}
                onAction={() => setOpenModal({ cardKey: 'default_pricing' })}
            />
        </div>
    );

    const contractorSections = (
        <>
            {flexCenters.map((flexCenter) => (
                <div key={flexCenter.client_id} className="flex flex-col gap-2">
                    <span className="text-[13px] font-semibold text-slate-900">{flexCenter.business_name}</span>
                    <div className="grid grid-cols-4 gap-4">
                        <SettingCard
                            card={flexCenter.pricingCard}
                            icon={CARD_ICONS.pricing}
                            onAction={() => setOpenModal({ cardKey: 'pricing', shipperId: flexCenter.client_id })}
                        />
                    </div>
                </div>
            ))}
            <div className="flex flex-col gap-3">
                <SectionHeading>Default for All Flex Centers</SectionHeading>
                <div className="grid grid-cols-4 gap-4">
                    <SettingCard
                        card={defaultPricingCard}
                        icon={CARD_ICONS.pricing}
                        onAction={() => setOpenModal({ cardKey: 'default_pricing' })}
                    />
                </div>
            </div>
        </>
    );

    const marketplaceSections = marketplaceShippers.map((shipper) => (
        <div key={shipper.client_id} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-slate-900">{shipper.business_name}</span>
                <CopyableId label="Shipper ID" value={shipper.client_id} />
            </div>
            <div className="grid grid-cols-4 gap-4">
                {shipper.cards.map((card) => (
                    <SettingCard
                        key={card.key}
                        card={card}
                        icon={CARD_ICONS[card.key]}
                        onAction={() => openCard(card, shipper)}
                    />
                ))}
            </div>
        </div>
    ));

    const isFlexCenter = isFlexCenterRow(row);
    const isFulfillmentPartner = isFulfillmentPartnerRow(row);

    let shipperSections;
    if (!shipperGroups.length) {
        shipperSections = (
            <span className="text-[13px] text-slate-500">
                {isFlexCenter ? NO_FLEX_CENTER_SHIPPERS_COPY : NO_LINKED_SHIPPERS_COPY}
            </span>
        );
    } else if (isFlexCenter) {
        shipperSections = <FlexCenterShippers shippers={shippers} onMap={openCard} />;
    } else {
        shipperSections = shipperGroups.map((group, index) => (
            <div key={group.key} className="flex flex-col gap-3">
                <LinkedShippers shippers={group.shippers} firstPosition={firstPositionByGroup[index]} />
                <div className="grid grid-cols-4 gap-4">
                    {group.cards.map((card) => (
                        <SettingCard
                            key={card.key}
                            card={card}
                            icon={CARD_ICONS[card.key]}
                            shippers={CARRIER_WIDE_CARDS.has(card.key) ? [] : group.shippers}
                            onAction={(shipper) => openCard(card, shipper)}
                        />
                    ))}
                </div>
            </div>
        ));
    }

    const defaultsSection = isFulfillmentPartner ? (
        <div className={SECTION_BLOCK_CLASS}>
            <SectionHeading>Defaults for All Shippers</SectionHeading>
            {defaultCards}
        </div>
    ) : null;

    const contractorList = contractors?.length ? (
        contractors.map(({ contractorRow, flexCenter }) => (
            <div
                key={contractorRow.client_id}
                className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
            >
                <div className="flex min-w-0 flex-wrap items-center gap-3 [&_button]:max-w-full [&_button>span]:truncate">
                    <span className="text-[13px] font-semibold text-slate-900">{contractorRow.business_name}</span>
                    <CopyableId label="Account ID" value={contractorRow.client_id} />
                    <button
                        type="button"
                        onClick={() => onOpenContractor?.(contractorRow)}
                        aria-label={`Open ${contractorRow.business_name}`}
                        className="ml-auto rounded-[6px] border border-[#eceff4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-800"
                    >
                        Open
                    </button>
                </div>
                <div className="grid grid-cols-4 gap-4">
                    <SettingCard
                        card={flexCenter.pricingCard}
                        icon={CARD_ICONS.pricing}
                        onAction={() => setOpenModal({ cardKey: 'pricing', contractorId: contractorRow.client_id })}
                    />
                    <DocumentsCard
                        compliance={contractorRow.compliance}
                        onUpload={(docType) =>
                            setOpenModal({ cardKey: 'compliance', docType, contractorId: contractorRow.client_id })
                        }
                    />
                </div>
            </div>
        ))
    ) : (
        <span className="text-[13px] text-slate-500">No Motor Carriers work under this Flex Center yet.</span>
    );

    const contractorsSection = contractors ? (
        <div className={SECTION_BLOCK_CLASS}>
            <SectionHeading>Motor Carriers at this Flex Center</SectionHeading>
            {contractorList}
        </div>
    ) : null;

    const settingModals = modalContractor ? (
        <ShipperSettingModals
            row={modalContractor.contractorRow}
            cardKey={openModal.cardKey}
            docType={openModal.docType}
            shipper={modalContractor.flexCenter}
            defaultPricing={modalContractor.defaultPricing}
            defaultIntegrations={[]}
            onClose={() => setOpenModal(null)}
        />
    ) : (
        <ShipperSettingModals
            row={row}
            cardKey={openModal?.cardKey}
            docType={openModal?.docType}
            shipper={modalShipper}
            defaultPricing={defaultPricing}
            defaultIntegrations={defaultIntegrations}
            onClose={() => setOpenModal(null)}
        />
    );

    // The carrier's own documents, once per row; a Flex Center files none, and shippers sign only a Rate Addendum.
    const documentsCard =
        row.isContractor || isFulfillmentPartner ? (
            <div className="grid grid-cols-4 gap-4">
                <DocumentsCard
                    compliance={row.compliance}
                    onUpload={(docType) => setOpenModal({ cardKey: 'compliance', docType })}
                />
            </div>
        ) : null;

    let partnersHeading = 'Shippers linked to this carrier';
    let partnerSections = shipperSections;
    if (row.isContractor) {
        partnersHeading = 'Flex Centers this Motor Carrier works under';
        partnerSections = contractorSections;
    }
    if (row.isMarketplace) {
        partnersHeading = 'Shippers priced on the marketplace';
        partnerSections = marketplaceSections;
    }

    return (
        <div className="flex flex-col gap-4 bg-white px-5 py-4">
            {documentsCard}

            <div className={SECTION_BLOCK_CLASS}>
                <SectionHeading>{partnersHeading}</SectionHeading>
                {partnerSections}
            </div>

            {defaultsSection}

            {contractorsSection}

            {settingModals}
        </div>
    );
};

export default ExpandedCarrier;
